import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Crown, ExternalLink, Star } from 'lucide-react';
import SEO from '../../components/SEO';

const ExploreBooks = () => {
  const subscriptionJoinLink = 'https://members.popcrownofficial.com/subscription/join';

  const books = [
    {
      title: 'Moonwalk',
      author: 'Michael Jackson',
      year: '1988',
      description: 'Michael\'s only autobiography, offering a rare glimpse into his life, career, and creative process.',
      category: 'Autobiography',
      essential: true
    },
    {
      title: 'Dancing the Dream',
      author: 'Michael Jackson',
      year: '1992',
      description: 'A collection of poems and reflections revealing Michael\'s inner thoughts on love, life, and nature.',
      category: 'Poetry',
      essential: true
    },
    {
      title: 'Michael Jackson: The Magic, The Madness, The Whole Story',
      author: 'J. Randy Taraborrelli',
      year: '2009',
      description: 'The definitive biography covering his entire life with unprecedented access and detail.',
      category: 'Biography',
      essential: true
    },
    {
      title: 'Remember the Time',
      author: 'Bill Whitfield & Javon Beard',
      year: '2014',
      description: 'Intimate stories from his personal bodyguards during his final years.',
      category: 'Memoir',
      essential: false
    },
    {
      title: 'Man in the Music',
      author: 'Joseph Vogel',
      year: '2011',
      description: 'A comprehensive analysis of Michael\'s complete musical catalog and creative genius.',
      category: 'Music Analysis',
      essential: true
    },
    {
      title: 'Michael Jackson, Inc.',
      author: 'Zack O\'Malley Greenburg',
      year: '2014',
      description: 'The business story behind the King of Pop\'s empire and lasting financial legacy.',
      category: 'Business',
      essential: false
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO title="Michael Jackson Books & Resources — Essential Reading | PopCrown" description="The definitive Michael Jackson reading list. Books about his music, life, legacy, and artistry. Curated by MJ fans for MJ fans." keywords="Michael Jackson books, MJ biography, Michael Jackson autobiography Moonwalk, Man in the Music, King of Pop books, Michael Jackson resources, MJ reading list" canonical="/explore/books" />
      {/* Header */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4">
          <Link to="/explore" className="inline-flex items-center gap-2 text-[#E10600] hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-5 h-5" />
            Back to Explore
          </Link>
          <div className="flex items-center gap-4">
            <BookOpen className="w-12 h-12 text-[#F97316]" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Books & <span className="thriller-text">Resources</span>
              </h1>
              <p className="text-gray-400">Essential reading for every fan</p>
            </div>
          </div>
        </div>
      </section>

      {/* Books Grid */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {books.map((book, index) => (
              <div
                key={index}
                className="p-6 rounded-xl transition-all duration-300 hover:transform hover:scale-105 relative"
                style={{
                  background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                  border: book.essential ? '2px solid #E10600' : '2px solid rgba(225, 6, 0, 0.2)'
                }}
              >
                {book.essential && (
                  <div className="absolute -top-3 -right-3">
                    <div className="bg-[#E10600] text-black px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <Star className="w-3 h-3" fill="black" />
                      Essential
                    </div>
                  </div>
                )}
                
                <div className="flex items-start justify-between mb-4">
                  <span className="text-xs px-2 py-1 rounded-full" style={{
                    background: 'rgba(225, 6, 0, 0.2)',
                    color: '#E10600'
                  }}>
                    {book.category}
                  </span>
                  <span className="text-gray-500 text-sm">{book.year}</span>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">{book.title}</h3>
                <p className="text-[#E10600] text-sm mb-3">by {book.author}</p>
                <p className="text-gray-400 text-sm mb-4">{book.description}</p>
                
                <a
                  href={`https://www.amazon.com/s?k=${encodeURIComponent(book.title + ' ' + book.author)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#F97316] hover:text-[#E10600] transition-colors text-sm"
                >
                  <span>Find on Amazon</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-10 h-10 mx-auto mb-4 object-contain" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Explore Our <span className="thriller-text">Book Collection</span>
          </h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Gold members get access to our complete book database with 50+ titles, summaries, and community discussions.
          </p>
          <a href={subscriptionJoinLink} target="_blank" rel="noopener noreferrer" className="btn-gold inline-block">
            Unlock Full Library
          </a>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-black">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 text-xs">
            Links to external retailers. PopCrown is not affiliated with any artist estates or rights holders.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ExploreBooks;
